import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface TbRow {
  sce_code: string;
  account_class: number;
  debit_tnd: string;
  credit_tnd: string;
}

interface AnnexeOut {
  annexe_number: number;
  annexe_label: string;
  status: 'ok' | 'imbalanced' | 'missing_data' | 'not_implemented';
  computed_lines: Array<{ line_code: string; line_label: string; amount_millimes: number; sce_codes_aggregated: string[] }>;
  totals: Record<string, number>;
}

interface LiassierResult {
  fiscal_year: number;
  company_name: string;
  annexes: AnnexeOut[];
  consistency_findings: Array<{ code: string; severity: 'error' | 'warning' | 'info'; description: string }>;
  overall_status: 'ready_for_review' | 'imbalanced' | 'partial';
}

const SAMPLE_TB: TbRow[] = [
  { sce_code: '101', account_class: 1, debit_tnd: '0.000', credit_tnd: '50000.000' },
  { sce_code: '213', account_class: 2, debit_tnd: '30000.000', credit_tnd: '0.000' },
  { sce_code: '411', account_class: 4, debit_tnd: '20000.000', credit_tnd: '0.000' },
  { sce_code: '532', account_class: 5, debit_tnd: '15000.000', credit_tnd: '0.000' },
  { sce_code: '401', account_class: 4, debit_tnd: '0.000', credit_tnd: '5000.000' },
  { sce_code: '607', account_class: 6, debit_tnd: '30000.000', credit_tnd: '0.000' },
  { sce_code: '707', account_class: 7, debit_tnd: '0.000', credit_tnd: '40000.000' },
];

const ANNEXE_LABELS: Record<number, string> = {
  1: 'Bilan',
  2: 'Compte de résultat',
  3: 'État des immobilisations',
  4: 'État des amortissements',
  5: 'Variation des capitaux propres',
  6: 'État de la TVA',
  7: 'État des provisions',
  8: 'Tableau de financement',
};

const STATUS_STYLE: Record<AnnexeOut['status'], string> = {
  ok: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  imbalanced: 'bg-red-100 text-red-900 border-red-300',
  missing_data: 'bg-amber-100 text-amber-900 border-amber-300',
  not_implemented: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

const OVERALL_STYLE: Record<LiassierResult['overall_status'], { label: string; cls: string }> = {
  ready_for_review: { label: 'Prête à relecture', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  imbalanced: { label: 'Déséquilibrée', cls: 'bg-red-100 text-red-900 border-red-300' },
  partial: { label: 'Partielle', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
};

export function LiassierPage() {
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [companyName, setCompanyName] = useState('Sahel Textile Industries');
  const [matricule, setMatricule] = useState('1234567A/M/A/000');
  const [tb, setTb] = useState<TbRow[]>(SAMPLE_TB);
  const [annexes, setAnnexes] = useState<number[]>([1, 2]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function updateRow(i: number, patch: Partial<TbRow>) {
    setTb((p) => p.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setTb((p) => [...p, { sce_code: '', account_class: 6, debit_tnd: '0.000', credit_tnd: '0.000' }]);
  }
  function removeRow(i: number) {
    setTb((p) => p.filter((_, idx) => idx !== i));
  }
  function toggleAnnexe(n: number) {
    setAnnexes((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n].sort((a, b) => a - b)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload = {
        fiscal_year: fiscalYear,
        company_name: companyName,
        company_matricule_fiscal: matricule,
        trial_balance: tb.map((r) => ({
          sce_code: r.sce_code,
          account_class: r.account_class,
          debit_total_millimes: tndStringToMillimes(r.debit_tnd),
          credit_total_millimes: tndStringToMillimes(r.credit_tnd),
        })),
        annexes_to_include: annexes,
      };
      const res = await api.invokeAgent('liassier', { payload, fiscal_reference_date: `${fiscalYear}-12-31` });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const totalDebit = tb.reduce((s, r) => s + tndStringToMillimes(r.debit_tnd), 0);
  const totalCredit = tb.reduce((s, r) => s + tndStringToMillimes(r.credit_tnd), 0);

  return (
    <AgentPageShell
      title="Liassier"
      module="Audit · Phase 2"
      model="Sonnet 4.6"
      description={
        <>
          Pré-remplit la liasse fiscale tunisienne — bootstrap : annexes 1 (bilan) et 2 (compte
          de résultat) avec contrôles inter-annexes. Les annexes 3-15 retournent un finding clair
          "non implémenté" pour signaler ce qui reste à compléter.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Société">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Raison sociale"><input className={inputCls} value={companyName} onChange={(e) => setCompanyName(e.target.value)} required /></Field>
            <Field label="Matricule fiscal"><input className={inputCls + ' font-mono'} value={matricule} onChange={(e) => setMatricule(e.target.value.toUpperCase())} required /></Field>
            <Field label="Année fiscale"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} /></Field>
          </div>
        </FormCard>

        <FormCard title="Annexes à inclure">
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => toggleAnnexe(n)}
                className={`px-2 py-1 rounded border text-xs transition ${
                  annexes.includes(n) ? 'bg-ink text-cream border-ink' : 'bg-white text-ink border-border hover:border-gold'
                }`}
                title={ANNEXE_LABELS[n]}
              >
                A{n} · {ANNEXE_LABELS[n]?.slice(0, 12)}
                {n > 2 && <span className="ml-1 text-[10px] opacity-60">(stub)</span>}
              </button>
            ))}
          </div>
        </FormCard>

        <FormCard
          title={`Balance générale (${tb.length} comptes)`}
          action={
            <div className="flex items-center gap-3 text-xs">
              <span className={totalDebit === totalCredit ? 'text-emerald-700' : 'text-red-700'}>
                D {formatMillimes(totalDebit, { withSymbol: false })} {totalDebit === totalCredit ? '=' : '≠'} C {formatMillimes(totalCredit, { withSymbol: false })}
              </span>
              <button type="button" onClick={addRow} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">+ Ligne</button>
            </div>
          }
        >
          <div className="overflow-x-auto -mx-2 max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-cream/40">
                <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                  <th className="px-2 py-2 text-left">Code SCE</th>
                  <th className="px-2 py-2 text-left">Classe</th>
                  <th className="px-2 py-2 text-right">Débit (TND)</th>
                  <th className="px-2 py-2 text-right">Crédit (TND)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tb.map((r, idx) => (
                  <tr key={idx} className="border-b border-border/50">
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1'} value={r.sce_code} onChange={(e) => updateRow(idx, { sce_code: e.target.value })} required /></td>
                    <td className="px-1 py-1">
                      <select className={inputCls + ' py-1'} value={r.account_class} onChange={(e) => updateRow(idx, { account_class: parseInt(e.target.value, 10) })}>
                        {[1, 2, 3, 4, 5, 6, 7].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1 text-right'} value={r.debit_tnd} onChange={(e) => updateRow(idx, { debit_tnd: e.target.value })} /></td>
                    <td className="px-1 py-1"><input className={inputCls + ' font-mono py-1 text-right'} value={r.credit_tnd} onChange={(e) => updateRow(idx, { credit_tnd: e.target.value })} /></td>
                    <td className="px-1">
                      {tb.length > 1 && <button type="button" onClick={() => removeRow(idx)} className="text-xs text-red-700">×</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FormCard>

        <button type="submit" disabled={loading || tb.length === 0} className="btn-primary">
          {loading ? 'Génération…' : 'Pré-remplir la liasse'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <LiassierResultView result={result as LiassierResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function LiassierResultView({ result }: { result: LiassierResult }) {
  const ov = OVERALL_STYLE[result.overall_status];
  return (
    <div className="space-y-5">
      <div className={`p-3 rounded border-2 ${ov.cls} flex items-center gap-3 flex-wrap`}>
        <span className="text-xs uppercase tracking-wider">Statut global</span>
        <strong className="font-serif">{ov.label}</strong>
        {result.consistency_findings.length > 0 && <span className="text-xs">{result.consistency_findings.length} finding(s) de cohérence</span>}
      </div>

      {result.consistency_findings.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Contrôles inter-annexes</div>
          <ul className="space-y-1 text-sm">
            {result.consistency_findings.map((f, i) => (
              <li key={i} className={`p-2 rounded text-xs border ${
                f.severity === 'error' ? 'border-red-300 bg-red-50 text-red-900' : f.severity === 'warning' ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-border bg-white'
              }`}>
                <span className="font-mono uppercase tracking-wider">{f.code}</span> · {f.description}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        {result.annexes.map((a) => {
          const stat = STATUS_STYLE[a.status];
          return (
            <details key={a.annexe_number} className="card !p-0 overflow-hidden border border-border" open={a.annexe_number <= 2}>
              <summary className="cursor-pointer px-4 py-3 flex items-center justify-between hover:bg-cream/40">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-sm">A{a.annexe_number}</span>
                  <span className="font-medium">{a.annexe_label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${stat}`}>{a.status}</span>
                </div>
                <span className="text-xs text-taupe">{a.computed_lines.length} ligne(s)</span>
              </summary>
              {a.computed_lines.length > 0 && (
                <div className="px-4 py-3 border-t border-border bg-cream/20">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                        <th className="px-2 py-1 text-left">Code</th>
                        <th className="px-2 py-1 text-left">Libellé</th>
                        <th className="px-2 py-1 text-left">Comptes agrégés</th>
                        <th className="px-2 py-1 text-right">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.computed_lines.map((l) => (
                        <tr key={l.line_code} className="border-b border-border/40">
                          <td className="px-2 py-1 font-mono text-xs">{l.line_code}</td>
                          <td className="px-2 py-1">{l.line_label}</td>
                          <td className="px-2 py-1 text-xs text-taupe font-mono">{l.sce_codes_aggregated.join(', ')}</td>
                          <td className="px-2 py-1 text-right font-mono">{formatMillimes(l.amount_millimes, { withSymbol: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                    {Object.keys(a.totals).length > 0 && (
                      <tfoot>
                        <tr className="bg-cream/40 font-medium">
                          <td colSpan={2} className="px-2 py-1">Totaux</td>
                          <td colSpan={2} className="px-2 py-1 text-right font-mono text-xs">
                            {Object.entries(a.totals).map(([k, v]) => (
                              <span key={k} className="ml-3">{k} : {formatMillimes(v, { withSymbol: false })}</span>
                            ))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </details>
          );
        })}
      </section>
    </div>
  );
}
